'use client'

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { fetchExchangeRates, getCurrencyFromCountry, ExchangeRates } from '@/lib/utils/currency-converter'
import { useUserLocation } from '@/lib/hooks/useUserLocation'

interface CurrencyContextType {
    currency: string
    exchangeRates: ExchangeRates | null
    isLoading: boolean
    setCurrency: (currency: string) => void
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined)

/**
 * Currency Provider Component
 * Manages currency conversion state across the application
 * Automatically detects user location and sets appropriate currency
 *
 * @param children - Child components to wrap
 *
 * @example
 * <CurrencyProvider>
 *   <App />
 * </CurrencyProvider>
 */
export function CurrencyProvider({ children }: { children: ReactNode }) {
    const [currency, setCurrencyState] = useState<string>('EUR')
    const [exchangeRates, setExchangeRates] = useState<ExchangeRates | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const { countryCode, isLoading: isLocationLoading } = useUserLocation()

    // Fetch exchange rates on mount
    useEffect(() => {
        const loadExchangeRates = async () => {
            try {
                const rates = await fetchExchangeRates()
                setExchangeRates(rates)
            } catch (error) {
                console.error('Failed to load exchange rates:', error)
            } finally {
                setIsLoading(false)
            }
        }

        loadExchangeRates()
    }, [])

    // Set currency based on user location

    useEffect(() => {
        if (!isLocationLoading && countryCode) {
            // Check if user has a saved currency preference
            const savedCurrency = getSavedCurrency()
            if (savedCurrency) {
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setCurrencyState(savedCurrency)
            } else {
                // Auto-detect currency from country
                const detectedCurrency = getCurrencyFromCountry(countryCode)
                setCurrencyState(detectedCurrency)
            }
        }
    }, [countryCode, isLocationLoading])

    // Save currency preference when it changes
    const setCurrency = (newCurrency: string) => {
        setCurrencyState(newCurrency)
        saveCurrency(newCurrency)
    }

    return (
        <CurrencyContext.Provider
            value={{
                currency,
                exchangeRates,
                isLoading: isLoading || isLocationLoading,
                setCurrency
            }}>
            {children}
        </CurrencyContext.Provider>
    )
}

/**
 * Hook to access currency context
 * @returns Currency context with current currency, exchange rates, and setter
 *
 * @example
 * const { currency, exchangeRates, setCurrency } = useCurrency()
 * const convertedPrice = formatPriceWithCurrency(100, currency, exchangeRates)
 */
export function useCurrency() {
    const context = useContext(CurrencyContext)
    if (context === undefined) {
        throw new Error('useCurrency must be used within a CurrencyProvider')
    }
    return context
}

/**
 * Saves currency preference to localStorage
 */
function saveCurrency(currency: string) {
    try {
        localStorage.setItem('preferred_currency', currency)
    } catch {
        // Ignore localStorage errors
    }
}

/**
 * Gets saved currency preference from localStorage
 */
function getSavedCurrency(): string | null {
    try {
        return localStorage.getItem('preferred_currency')
    } catch {
        return null
    }
}
